// Copy for /jumpstart-mw-ri (also served at /jumpstart-mw-rf and /jumpstart-mw-ri-trial), verbatim from Martin's
// HTML prototype (jumpstart-mw-ri.html, 2 November 2026): the raising investment page sold as the 7-day free
// trial, rebuilt round one argument. What every trial page says is in trial.js. The 19 October page and the
// 26 October trial page it replaces are in git before this commit (raisingInvestment.js, raisingInvestmentTrial.js).
import { ASSET_PATH } from '../../content'
import * as shared from './shared'
import * as trialCopy from './trial'

const META = {
  title: 'Jumpstart: Raising Investment | Find out what’s stopping investors saying yes',
  description:
    'Find out what’s stopping investors saying yes, and fix it in 30 days with Martin Warner. 7 days free, then $34.95.',
}

// the prototype's buy button and the lines round it; the card's cancel line names the account page only
const trial = {
  ...trialCopy.trial,
  ctaSub: '7 days free, then {price}',
  note: '**7 days free.** Billed **{price}** on day 8. Cancel anytime during the free trial and pay nothing.',
  sticky: { line: '**7 days free**, then {price}', cta: 'Start free trial' },
  card: {
    ...trialCopy.trial.card,
    title: '30-day Jumpstart · Raising Investment',
    flow: [
      ['Today', '{zero}'],
      ['Days 1–7', 'Free'],
      ['Day 8', '{price}'],
    ],
    terms:
      '**Trial terms.** A card is required to start. You are not charged for 7 days; on day 8 it is charged {price} once. Cancel anytime during the free trial from your account page to avoid the charge.',
  },
}

const hero = {
  ...trialCopy.hero,
  eyebrow: 'Jumpstart · Raising Investment',
  headline: 'Need investment for your business?',
  headlineAccent: 'Find out what’s stopping investors saying yes, and fix it in 30 days.',
  // the accent on its own line, as the prototype breaks it
  accentBlock: true,
  lede: 'Investors rarely tell you the real reason they say no. Martin will, then he helps you fix it, whether you’ve pitched fifty investors or none yet.',
  // bold runs are marked **like this**
  checks: [
    '**Find out in about 20 minutes** what investors will see, and what to fix first',
    '**Fix it with Martin:** your pitch, proof, numbers and who to approach',
    '**Ask Martin anything about your raise, any time,** plus live Q&As and weekly deep dives',
    '**Finish day 30 investor-ready**, with Martin’s written review and plan',
  ],
  byline: {
    photo: { src: `${ASSET_PATH}/martin-warner-dark.jpg`, alt: '' },
    text: '**With Martin Warner:** founder and investor, $50MM exit, USA Today best-selling author',
  },
}

const video = {
  ...shared.video,
  eyebrow: null,
  headline: 'Hear from Martin:',
  headlineAccent: 'why investors say no, and how to fix it',
  label: 'Watch: why investors say no, and how to fix it',
  playLabel: 'Play the video: Hear from Martin why investors say no, and how to fix it',
  // the raising investment cuts, one per market since the price is spoken (Anthony, 2 November 2026)
  wids: { GB: { landscape: 'qgoknqdwuf' }, US: { landscape: 'yayf9srful' } },
}

const proof = {
  big: null,
  text: '“Martin helped me think like an investor, and I finally closed my first round. Thank you.”',
  name: 'Mike Stanton',
  sub: 'Real results from founders Martin has mentored.',
}

// "Sound familiar?" as six pains and the reframe under them, not the tabs the other focus pages show
const problem = {
  eyebrow: 'Sound familiar?',
  headline: 'Raising money is hard',
  headlineAccent: 'when nobody tells you what’s wrong.',
  pains: [
    'You don’t know where to start, or which investors to approach',
    'You’ve sent your pitch out and heard nothing back',
    'Investors say “too early” and you don’t know what that really means',
    'You’re not sure how much to ask for, or how much of your business to give away',
    'You’re worried you’ll only get one chance with the right people',
    'Meanwhile, time and savings are running down',
  ],
  reframe: {
    text: '**It’s rarely your idea.** Investors weigh up the same handful of things, and they rarely tell you which one put them off. Jumpstart shows you which one, and Martin helps you fix it.',
    link: 'Find your #1 blocker in one tap ↓',
  },
}

// "Find your #1 blocker": one tap in place of the sliders; the tap's key is the visitor's level for Meta
const blocker = {
  eyebrow: 'Find your #1 blocker',
  headline: 'What’s holding',
  headlineAccent: 'your raise back?',
  lede: 'Tap the one that sounds most like you, and see what to do about it.',
  tapNote: 'Tap an answer to see your #1 blocker and what to do first.',
  kicker: 'Your #1 blocker',
  // round the blocker's `fix`
  fix: ['Jumpstart works on ', ' with you from day 1, with Martin’s feedback.'],
  options: [
    {
      key: 'pitch',
      label: 'I can’t explain my business quickly',
      name: 'Your pitch',
      fix: 'your pitch',
      intro:
        'Investors cannot yet repeat your story back in a sentence, and an unclear story is the quickest way to lose a meeting.',
      bullets: [
        'Write your pitch in three sentences: the problem, your solution and why now',
        'Tell it to five people outside your business and ask them to repeat it back',
        'Cut anything that does not help an investor make a decision',
      ],
    },
    {
      key: 'proof',
      label: 'Investors say it’s too early',
      name: 'Proof people want it',
      fix: 'your proof',
      intro: 'Investors say “too early” when they cannot see evidence that people want what you are building.',
      bullets: [
        'List every signal you already have: customers, pilots, waiting lists, revenue or letters of intent',
        'Run one small test that produces a clear result within 30 days',
        'Lead with your strongest proof in the first minute of every pitch',
      ],
    },
    {
      key: 'market',
      label: 'I’m not sure the market is big enough',
      name: 'Size of the opportunity',
      fix: 'how you show the opportunity',
      intro:
        'Investors need to see a market large enough to return their money, and a believable way for you to win a share of it.',
      bullets: [
        'Estimate the number of customers and the value of each, built from the bottom up',
        'Show who you are replacing or beating today',
        'Be honest about the part of the market you can reach first',
      ],
    },
    {
      key: 'numbers',
      label: 'My numbers aren’t solid yet',
      name: 'Your numbers',
      fix: 'your numbers',
      intro: 'Your numbers need to be clear enough that you can answer any question about them without notes.',
      bullets: [
        'Know what it costs you to win a customer and what a customer is worth over time',
        'Build a simple 12 to 18 month forecast with your assumptions written down',
        'Be ready to explain what changes if a key assumption turns out wrong',
      ],
    },
    {
      key: 'team',
      label: 'It’s just me, or my team is thin',
      name: 'Your team',
      fix: 'how you present your team',
      intro: 'Investors back people as much as ideas. They want to see why you are the team to make it work.',
      bullets: [
        'Write down what each person has done that matters for this business',
        'Name the gaps honestly and how you plan to fill them',
        'Show who is fully committed, and how',
      ],
    },
    {
      key: 'investors',
      label: 'I don’t know which investors to approach',
      name: 'Knowing which investors to approach',
      fix: 'who you approach',
      intro:
        'You are not yet sure which investors fit your business, so effort goes into conversations that cannot turn into a yes.',
      bullets: [
        'Define the kind of investor who backs businesses like yours, at your stage and size',
        'Build a shortlist and rank it by fit, not by how famous the names are',
        'Look for a warm introduction to each one rather than approaching cold',
      ],
    },
    {
      key: 'funds',
      label: 'I’m not sure how much to raise, or what for',
      name: 'What the money is for',
      fix: 'your use of funds',
      intro: 'Investors want to know exactly what their money will achieve and what it will unlock next.',
      bullets: [
        'Break the amount you are raising into three or four clear uses',
        'Link each use to a milestone an investor can measure',
        'Show what the business looks like once the money is spent',
      ],
    },
  ],
}

const journey = {
  eyebrow: 'Your 30 days',
  headline: 'How Jumpstart gets you',
  headlineAccent: 'investor-ready in 30 days',
  lede: null,
  columns: 2,
  steps: [
    {
      when: 'Day 1 · Assess',
      title: 'Investor-Readiness Assessment',
      text: 'About 20 minutes. It scores what investors weigh up, and Martin reviews it himself to name your #1 blocker.',
    },
    {
      when: 'Weeks 1–3 · Fix',
      title: 'Deep dives and investment templates',
      text: 'A deep dive presentation each week, plus three 20-minute templates done on your own business: investor story, investor shortlist, and use of funds and milestones.',
    },
    {
      when: 'All month · Ask',
      title: 'Martin, whenever you’re stuck',
      text: '24/7 mentoring with replies typically within 24 to 48 hours, 2 live Q&As where you can bring your pitch or deck, and a short daily insight.',
    },
    {
      when: 'Day 30 · Review',
      title: 'Full Investor-Readiness Review',
      text: 'Retake the full assessment, with Martin’s written review: are you ready to pitch, what to fix next and who to approach first.',
    },
  ],
  deepDives: {
    title: 'Your 4 deep dives',
    items: [
      'What investors look for, and what makes them say no',
      'Your pitch: an investable story in two minutes',
      'Your numbers: how much to raise, valuation and how much to give away',
      'Finding the right investors and running your raise',
    ],
  },
  finish: {
    title: 'By day 30 you’ll have',
    items: [
      'An investor story you can tell in two minutes',
      'Your ask: how much, what for, and what it unlocks',
      'A ranked shortlist of investors who fit you',
      'Martin’s written review and your next step',
    ],
  },
  beyond: {
    kicker: 'Beyond the raise',
    title: 'Investors back whole businesses, so Jumpstart covers yours.',
    text: 'Your mentoring, live Q&As and daily insights aren’t limited to the raise. Bring questions on customers, pricing, your team or anything else, and your Day 30 review includes your next step for the business, not just the pitch.',
  },
}

// the gradient panel under "Your 30 days"
const invite = {
  kicker: 'Your 30-day journey',
  headline: 'Join an exhilarating 30-day journey to get investor-ready, and much more.',
  must: 'Don’t pitch another investor until you’ve done it.',
  text: 'Over 30 days, Martin shows you exactly what investors see, helps you fix what’s holding your raise back, and teaches you far more about building a business along the way. You finish with clear next steps to grow as an entrepreneur, and as a business.',
  items: [
    'See your business through an investor’s eyes',
    'Fix your #1 blocker, with Martin alongside you',
    'Finish with next steps for you and your business',
  ],
  cta: 'Start your 30-day journey',
  fine: 'A month packed with value, and it starts free.',
}

const mentor = {
  ...trialCopy.mentor,
  stats: [
    { figure: '$50MM', text: 'UK tech record exit, 17 months after co-founding botObjects' },
    { figure: '120+', text: 'Patents' },
    { figure: '12', text: 'Products built and taken to market' },
    { figure: '24', text: 'Years teaching entrepreneurship' },
  ],
  credentials: [
    ['USA Today best-selling author of ', 'The Startup Story: An Entrepreneur’s Journey from Idea to Exit'],
    ['First British entrepreneur to build an electric aviation aircraft'],
    ['No. 1 in T3 Magazine’s Top 100 products for his ProDesk3D printer'],
  ],
  interviews: null,
}

// four raising stories and the member videos, under the mentor's bio rather than in a section of their own
const testimonials = {
  ...shared.testimonials,
  title: 'What founders say about raising with Martin',
  quotes: [
    {
      tag: 'Angel round',
      quote: '“Completing my round of angel investment would not be possible without Martin’s guidance.”',
      name: 'Tom Matherson',
    },
    {
      tag: 'Angels vs venture capital',
      quote:
        '“Martin helped me understand the difference in preparation between angel investing and venture capital – it changed everything. Thank you!”',
      name: 'Robert Kingsley',
    },
    {
      tag: 'Valuation',
      quote:
        '“I struggled with my business valuation, Martin helped me realise how investors would think, and how to position myself.”',
      name: 'Carol Whitehead',
    },
    {
      tag: 'Pitch preparation',
      quote:
        '“Preparing for investment was harder than I thought, Martin broke down my business plan and showed me where I needed to focus so that I could pitch successfully.”',
      name: 'Sam Brown',
    },
  ],
  videosTitle: trialCopy.memberVideos.title,
  videos: trialCopy.memberVideos.slugs,
  // swipeHint: 'Swipe for more videos →', // retired 3 November 2026
  disclaimer: ['Individual results vary. See our ', 'Earnings and Income Disclaimer', '.'],
}

// the price card alone: the prototype has no list of what is included above it
const offer = {
  eyebrow: 'Start today',
  headline: 'Start free today.',
  headlineAccent: '{price} only if you stay.',
  lede: 'One avoidable mistake in a raise can cost you months, or a bigger share of your company than you needed to give.',
  stack: null,
  card: { label: 'Jumpstart offer' },
}

const compare = {
  // after the price card and before the founders' questions (2 November 2026)
  beforeFaq: true,
  eyebrow: 'What’s different?',
  headline: 'Why not keep pitching,',
  headlineAccent: 'or pay an adviser?',
  lede: 'Honest feedback from a founder who has built, sold and backed businesses, for {price} rather than a retainer or a slice of your company.',
  // each option's rows, marked yes, no or mixed
  options: [
    {
      title: 'Jumpstart',
      me: true,
      rows: [
        { label: 'Cost', text: '{price} once', mark: 'yes' },
        { label: 'Start', text: 'Today', mark: 'yes' },
        { label: 'Equity', text: 'Keep all of it', mark: 'yes' },
        { label: 'Feedback', text: 'Honest, from Martin', mark: 'yes' },
      ],
    },
    {
      title: 'Going it alone',
      rows: [
        { label: 'Cost', text: 'Free, but costs months', mark: 'mixed' },
        { label: 'Start', text: 'Today', mark: 'yes' },
        { label: 'Equity', text: 'Keep all of it', mark: 'yes' },
        { label: 'Feedback', text: 'Rarely told why', mark: 'no' },
      ],
    },
    {
      title: 'Fundraising advisers',
      rows: [
        { label: 'Cost', text: 'Retainers, success fees, or both', mark: 'no' },
        { label: 'Start', text: 'Varies', mark: 'mixed' },
        { label: 'Equity', text: 'Usually keep it', mark: 'mixed' },
        { label: 'Feedback', text: 'Included', mark: 'yes' },
      ],
    },
    {
      title: 'Accelerators',
      rows: [
        { label: 'Cost', text: 'Paid for in equity', mark: 'no' },
        { label: 'Start', text: 'Competitive application', mark: 'no' },
        { label: 'Equity', text: 'Usually give some up', mark: 'no' },
        { label: 'Feedback', text: 'Included', mark: 'yes' },
      ],
    },
  ],
  marks: { yes: 'Yes', no: 'No', mixed: 'Partly' },
  note: 'General comparison. Costs and terms vary by adviser and accelerator.',
}

const faq = {
  ...shared.faq,
  headline: 'Questions',
  headlineAccent: 'founders ask',
  items: [
    {
      q: 'How does the free trial work?',
      a: 'Start today for {zero} and try Jumpstart for 7 days. On day 8 your card is billed {price}, once. Cancel anytime during the free trial from your account page and you pay nothing.',
    },
    {
      q: 'I haven’t approached any investors yet. Is this for me?',
      a: 'Yes, and it is the best time to start. You rarely get a second first meeting, so getting investor-ready before you approach anyone protects your best contacts.',
    },
    {
      q: 'I’m already pitching. Is it too late?',
      a: 'No. You will find out what is making investors hesitate and fix it between meetings, so your next conversations go differently.',
    },
    {
      q: 'Does Jumpstart introduce me to investors?',
      a: 'Jumpstart doesn’t promise introductions. It gets you investor-ready and helps you build a shortlist of the right investors and plan how to reach them, ideally through warm introductions.',
    },
    {
      q: 'What kind of investment does it cover?',
      a: 'Mainly angel investors and venture capital, the usual routes for early-stage founders. Much of it also helps with a friends-and-family round.',
    },
    {
      q: 'Is it only about raising money?',
      a: 'Raising is the focus, but investors back whole businesses. Your mentoring, live Q&As and daily insights cover the rest of your business too, and your Day 30 review includes your next step for the business, not just the raise.',
    },
    {
      q: 'How much time does it take?',
      a: 'Allow 2 to 3 hours a week. Your Investor-Readiness Assessment takes about 20 minutes, and each investment template takes about 20 minutes.',
    },
    {
      q: 'Do I really get Martin?',
      a: 'Yes. Martin reviews your work himself, answers your questions in the live Q&As and replies in the 24/7 mentoring portal, typically within 24 to 48 hours.',
    },
    {
      q: 'Does Jumpstart guarantee I will raise investment?',
      a: 'No, and nobody honestly can. Jumpstart gives you a diagnosis, a plan and Martin’s feedback; what you raise depends on your business and what you do with them. See our Earnings and Income Disclaimer.',
    },
  ],
}

const finalCta = {
  ...trialCopy.finalCta,
  headline: 'Walk into your next pitch',
  headlineAccent: 'knowing what investors see.',
  lede: 'A month from now, you could still be guessing why investors hesitate. Or you could have fixed it, with Martin’s written review behind you.',
  // the billing line under the closing button, as under the hero's
  line: 'note',
}

const content = {
  key: 'ri',
  trial,
  // sections whose pieces are narrower than the column read centred from tablet width up (hero, proof, closing)
  centred: true,
  META,
  trusted: shared.trusted,
  hero,
  video,
  proof,
  // the member videos sit with the founders' quotes under the mentor's bio
  memberVideos: null,
  problem,
  blocker,
  // no sliders, no drop-out chart and no ladder: the blocker above stands for all three
  scorer: null,
  transformation: null,
  stage: null,
  journey,
  invite,
  advice: null,
  compare,
  mentor,
  testimonials,
  offer,
  faq,
  finalCta,
}

export default content
